import React, { useEffect, useState } from 'react'
import { Card, Button, Avatar, Space, Tag, Typography, Spin, Row, Col, theme, Pagination, Divider } from 'antd'
import {
  UserOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  MessageOutlined,
  PhoneOutlined,
  VideoCameraOutlined,
  HomeOutlined,
  EyeOutlined,
  SearchOutlined,
  ArrowLeftOutlined,
  FilterOutlined
} from '@ant-design/icons'
import PlaceholderImage from '../../../../assets/images/MH-logo-v1 1.png'

const { Text, Title } = Typography

const SearchResultsMobile = ({
  results = [],
  loading = false,
  onViewAvailability,
  searchFilters = {},
  searchType,
  currentPage = 1,
  totalPages = 1,
  totalResults = 0,
  onPageChange,
  onBackToRefinement
}) => {
  const [showResults, setShowResults] = useState(!loading)
  const [loadingStartTime, setLoadingStartTime] = useState(null)
  const { token } = theme.useToken()

  // Track loading start time
  useEffect(() => {
    if (loading) {
      setLoadingStartTime(Date.now())
      setShowResults(false)
    } else if (loadingStartTime) {
      const elapsed = Date.now() - loadingStartTime
      const minimumLoadingTime = 1500 // Reduced for mobile

      if (elapsed < minimumLoadingTime) {
        const remainingTime = minimumLoadingTime - elapsed
        setTimeout(() => {
          setShowResults(true)
        }, remainingTime)
      } else {
        setShowResults(true)
      }
    }
  }, [loading, loadingStartTime])

  const handleViewProfile = (doctor) => {
    onViewAvailability?.(doctor)
  }

  const displayResults = results.length > 0 ? results : []

  // Helper functions
  const getDoctorName = (doctor) => {
    return `${doctor.firstName} ${doctor.lastName}`.trim()
  }

  const isDoctorActive = (doctor) => {
    return doctor.extraData?.isActive !== false
  }

  const formatNextAvailableSlot = (nextAvailableSlot) => {
    if (!nextAvailableSlot || !nextAvailableSlot.date || !nextAvailableSlot.timeSlot) {
      return 'No availability'
    }

    const { date, timeSlot } = nextAvailableSlot
    const slotDate = new Date(date)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)

    let dateText = ''
    if (slotDate.toDateString() === today.toDateString()) {
      dateText = 'today'
    } else if (slotDate.toDateString() === tomorrow.toDateString()) {
      dateText = 'tomorrow'
    } else {
      const dayDiff = Math.ceil((slotDate - today) / (1000 * 60 * 60 * 24))
      if (dayDiff <= 7) {
        dateText = slotDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
      } else {
        dateText = slotDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
    }

    return `Available ${dateText} at ${timeSlot.time}`
  }

  const MobileDoctorCard = ({ doctor }) => (
    <Card
      style={{
        marginBottom: '12px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        opacity: isDoctorActive(doctor) ? 1 : 0.7,
        ...(doctor._id === 'virtual-doctor-24-7' && {
          background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
          border: 'none',
          boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)'
        })
      }}
      bodyStyle={{
        padding: '16px',
        ...(doctor._id === 'virtual-doctor-24-7' && {
          color: 'white'
        })
      }}
    >
      {/* Header with Avatar and Basic Info */}
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
        <Avatar
          size={60}
          src={doctor.image || PlaceholderImage}
          icon={<UserOutlined />}
          style={{
            border:
              doctor._id === 'virtual-doctor-24-7'
                ? '2px solid rgba(255, 255, 255, 0.8)'
                : `2px solid ${token.colorPrimary}`,
            marginRight: '12px',
            flexShrink: 0
          }}
        />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
            <Title
              level={5}
              style={{
                margin: 0,
                color: doctor._id === 'virtual-doctor-24-7' ? 'white' : '#111827',
                fontSize: '16px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1
              }}
            >
              {getDoctorName(doctor)}
            </Title>
            {doctor._id === 'virtual-doctor-24-7' && (
              <Tag
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontWeight: '600',
                  fontSize: '10px',
                  marginLeft: '8px'
                }}
              >
                Featured
              </Tag>
            )}
          </div>

          <Text
            style={{
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.9)' : '#666',
              fontSize: '14px',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {doctor.extraData.profession?.charAt(0).toUpperCase() + doctor.extraData.profession?.slice(1).toLowerCase()}
          </Text>
        </div>
      </div>

      {/* Location and Availability Info */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
          <EnvironmentOutlined
            style={{
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'red',
              marginRight: '8px',
              flexShrink: 0
            }}
          />
          <Text
            style={{
              fontSize: '13px',
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#666',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1
            }}
          >
            {doctor.clinicRecord?._id === 'virtual-doctor-24-7' ? (
              'Virtual Consultation Available'
            ) : doctor.clinicRecord?.name ? (
              <>
                {doctor.clinicRecord.name}
                {doctor.clinicRecord?.distanceFormatted && (
                  <span
                    style={{
                      marginLeft: '8px',
                      color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.6)' : '#52c41a',
                      fontWeight: '500'
                    }}
                  >
                    • {doctor.clinicRecord.distanceFormatted}
                  </span>
                )}
              </>
            ) : (
              'Location not specified'
            )}
          </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px' }}>
          <CalendarOutlined
            style={{
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'blue',
              marginRight: '8px',
              flexShrink: 0
            }}
          />
          <Text
            style={{
              fontSize: '13px',
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#666'
            }}
          >
            {doctor._id === 'virtual-doctor-24-7'
              ? 'Available 24/7 - Instant Access'
              : formatNextAvailableSlot(doctor.nextAvailableSlot)}
          </Text>
        </div>

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ThunderboltOutlined
            style={{
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : 'goldenrod',
              marginRight: '8px',
              flexShrink: 0
            }}
          />
          <Text
            style={{
              fontSize: '13px',
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#666'
            }}
          >
            Instant Booking Available
          </Text>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <Button
            type='text'
            icon={<MessageOutlined />}
            size='small'
            style={{
              background: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6',
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#6b7280',
              border: doctor._id === 'virtual-doctor-24-7' ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
              width: '32px',
              height: '32px'
            }}
          />
          <Button
            type='text'
            icon={<PhoneOutlined />}
            size='small'
            style={{
              background: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6',
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#6b7280',
              border: doctor._id === 'virtual-doctor-24-7' ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
              width: '32px',
              height: '32px'
            }}
          />
          <Button
            type='text'
            icon={<VideoCameraOutlined />}
            size='small'
            style={{
              background: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6',
              color: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.8)' : '#6b7280',
              border: doctor._id === 'virtual-doctor-24-7' ? '1px solid rgba(255, 255, 255, 0.3)' : 'none',
              width: '32px',
              height: '32px'
            }}
          />
        </div>

        <Button
          type='primary'
          size='large'
          icon={<EyeOutlined />}
          onClick={() => handleViewProfile(doctor)}
          disabled={!isDoctorActive(doctor)}
          style={{
            flex: 1,
            height: '40px',
            background: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.9)' : token.colorPrimary,
            borderColor: doctor._id === 'virtual-doctor-24-7' ? 'rgba(255, 255, 255, 0.9)' : token.colorPrimary,
            color: doctor._id === 'virtual-doctor-24-7' ? '#3b82f6' : 'white',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          View Profile
        </Button>
      </div>
    </Card>
  )

  if (loading || !showResults) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          minHeight: '60vh',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: '16px',
          margin: '16px'
        }}
      >
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px'
          }}
        >
          <div
            style={{
              width: '60px',
              height: '60px',
              border: '3px solid rgba(59, 130, 246, 0.2)',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              position: 'absolute'
            }}
          />
          <div
            style={{
              width: '40px',
              height: '40px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'pulse 2s infinite'
            }}
          >
            <SearchOutlined style={{ fontSize: '18px', color: 'white' }} />
          </div>
        </div>

        <Title
          level={4}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: '0 0 8px 0',
            fontSize: '18px',
            textAlign: 'center'
          }}
        >
          Finding Your Perfect Doctor
        </Title>
        <Text style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>
          Searching through our network...
        </Text>
      </div>
    )
  }

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Navigation */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fff',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type='text'
              icon={<ArrowLeftOutlined />}
              onClick={onBackToRefinement}
              style={{ marginRight: '12px' }}
            />
            <div>
              <Title level={5} style={{ margin: 0, fontSize: '16px', color: '#111827' }}>
                {totalResults > 0 ? `${totalResults} Doctors Found` : 'Search Results'}
              </Title>
              {totalResults > 0 && (
                <Text type='secondary' style={{ fontSize: '12px' }}>
                  Page {currentPage} of {totalPages}
                </Text>
              )}
            </div>
          </div>
          <Button
            type='text'
            icon={<FilterOutlined />}
            onClick={onBackToRefinement}
            style={{ color: token.colorPrimary }}
          >
            Filters
          </Button>
        </div>
      </div>

      {/* Scrollable Results Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }} className='mobile-search-content'>
        {/* Results List */}
        {displayResults.length > 0 ? (
          <>
            {displayResults.map((doctor) => (
              <MobileDoctorCard key={doctor._id} doctor={doctor} />
            ))}

            {/* Mobile Pagination */}
            {totalPages > 1 && (
              <div style={{ textAlign: 'center', marginTop: '24px', marginBottom: '80px' }}>
                <Pagination
                  current={currentPage}
                  total={totalResults}
                  pageSize={10}
                  onChange={onPageChange}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} doctors`}
                  style={{ fontSize: '14px' }}
                />
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              background: '#f9fafb',
              borderRadius: '12px',
              marginTop: '20px'
            }}
          >
            <SearchOutlined style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
            <Title level={4} style={{ color: '#9ca3af', margin: '0 0 8px 0' }}>
              No Doctors Found
            </Title>
            <Text style={{ color: '#6b7280' }}>Try adjusting your search filters or location</Text>
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchResultsMobile
